import './ConfirmPrompt.scss'
import { useRef, useSyncExternalStore } from 'react'
import { Button, type ButtonProps } from '../Button'
import { Dialog } from '../Dialog'
import { cancelAction, defaultFocus, orderedActions, promptRole, type PromptTone } from './queue'
import { getHead, resolveHead, subscribe, type PromptEntry } from './store'

const variantFor: Record<PromptTone, NonNullable<ButtonProps['variant']>> = {
  neutral: 'secondary',
  primary: 'primary',
  danger: 'danger'
}

/**
 * Renders the head of the prompt queue. Mount once (the app shell does);
 * code anywhere calls `confirm()` / `confirmDelete()`.
 */
export function ConfirmHost() {
  const head = useSyncExternalStore(subscribe, getHead, getHead)
  // Keyed per call: each prompt gets a fresh focus trap and restore target.
  return head ? <PromptDialog key={head.key} entry={head} /> : null
}

function PromptDialog({ entry }: { entry: PromptEntry }) {
  const { request } = entry
  const surfaceRef = useRef<HTMLElement>(null)
  const focusRef = useRef<HTMLButtonElement>(null)
  const focused = defaultFocus(request)
  const cancel = cancelAction(request)

  return (
    <Dialog
      isOpen
      ref={surfaceRef}
      className="confirm-prompt"
      size="sm"
      role={promptRole(request)}
      title={request.title}
      description={request.message}
      isDismissable={cancel !== null}
      initialFocus={focused ? focusRef : surfaceRef}
      onClose={() => {
        if (cancel) resolveHead(entry.key, cancel.id)
      }}
      footer={orderedActions(request).map((action) => (
        <Button
          key={action.id}
          ref={action === focused ? focusRef : undefined}
          variant={variantFor[action.tone ?? 'neutral']}
          {...(action.icon != null && !action.cancel && { icon: action.icon })}
          onClick={() => {
            resolveHead(entry.key, action.id)
          }}
        >
          {action.label}
        </Button>
      ))}
    />
  )
}
